import request from '../../../app/utils/request';
import { config } from '../../../config';

import type {
  DataLakeMetadata,
  DeleteAssociatedEventsBody,
  DeleteAssociatedRulesBody,
  GetContentIdsAssociatedToEventId,
  PostAssociatedEventsBody,
  PostAssociatedRulesBodyV2,
  PostRule,
  Rule,
} from './request.type';

import { HTTP_METHOD } from '../../../_types/http-method';
import { MODULE_CLAIM } from '../../users/_constants/module-claim';

/**
 * ---
 * Rules
 * ---
 */

export function getRules(): Promise<Rule[]> {
  return request<Rule[]>({
    url: `${config().CMS_API.v1}/rules`,
    options: { method: HTTP_METHOD.get },
  });
}

export function getRuleById({ id }: { id: string }): Promise<Rule> {
  return request<Rule>({
    url: `${config().CMS_API.v1}/rules/${id}`,
    options: { method: HTTP_METHOD.get },
  });
}

export function postRule({ body }: { body: PostRule }): Promise<Rule> {
  return request<Rule>({
    url: `${config().CMS_API.v1}/rules`,
    options: { method: HTTP_METHOD.post, body },
  });
}

export function putRule({ id, body }: { id: string; body: PostRule }): Promise<Rule> {
  return request<Rule>({
    url: `${config().CMS_API.v1}/rules/${id}`,
    options: { method: HTTP_METHOD.put, body },
  });
}

export function deleteRule({ id }: { id: string }) {
  return request<void>({
    url: `${config().CMS_API.v1}/rules/${id}`,
    options: { method: HTTP_METHOD.delete },
  });
}

/**
 * ---
 * Metadata
 * ---
 */

export function getDataLakeMetadata() {
  return request<DataLakeMetadata[]>({
    url: `${config().CMS_API.v1}/personalization-metadata/v1`,
    options: { method: HTTP_METHOD.get },
  });
}

/**
 * ---
 * Rules association
 * ---
 */

export function getAssociatedRules({ id, type }: { id: string; type: string }) {
  return request<Rule[]>({
    url: `${config().CMS_API.v1}/rules/${type}/${id}`,
    options: { method: HTTP_METHOD.get },
  });
}

export function postAssociatedRulesV2({ body }: { body: PostAssociatedRulesBodyV2[] }) {
  return request<void>({
    url: `${config().CMS_API.v2}/rules/associations`,
    options: {
      method: HTTP_METHOD.post,
      body,
    },
  });
}

export function deleteAllAssociatedRules({ body }: { body: DeleteAssociatedRulesBody[] }) {
  return request<void>({
    url: `${config().CMS_API.v1}/rules/associations`,
    options: {
      method: HTTP_METHOD.delete,
      body,
    },
  });
}

/**
 * ---
 * Events association
 * At the moment, this association only applies to rule_groups.
 * ---
 */

export function getAssociatedEvents({ contentId }: { contentId: string }) {
  return request<{ id: string; eventName: string; [key: string]: any }[]>({
    url: `${config().CMS_API.v1}/rule-events/${MODULE_CLAIM.ruleGroups}/${contentId}`,
    options: { method: HTTP_METHOD.get },
  });
}

export function getContentIdsAssociatedToEventId({ eventId }: { eventId: string }) {
  return request<GetContentIdsAssociatedToEventId>({
    url: `${config().CMS_API.v1}/rule-events/${eventId}/content`,
    options: { method: HTTP_METHOD.get },
  });
}

export function postAssociatedEvents({ body }: { body: PostAssociatedEventsBody[] }) {
  return request<void>({
    url: `${config().CMS_API.v1}/rule-events/associations`,
    options: {
      method: HTTP_METHOD.post,
      body,
    },
  });
}

export function deleteAllAssociatedEvents({ body }: { body: DeleteAssociatedEventsBody[] }) {
  return request<void>({
    url: `${config().CMS_API.v1}/rule-events/associations`,
    options: {
      method: HTTP_METHOD.delete,
      body,
    },
  });
}
